import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const clienteApi = axios.create({
  baseURL: API_BASE,
});

clienteApi.interceptors.request.use((config) => {
  const token = sessionStorage.getItem("brave_cliente_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // v0.5.5 — Injeta a portaria atual selecionada no PortariaSwitcher.
  // Backend lê esses headers para hooks de notificação (Telegram etc.)
  // e auditoria de acesso por portaria.
  try {
    const raw = sessionStorage.getItem("brave_cliente_portaria");
    if (raw) {
      const p = JSON.parse(raw);
      if (p?.id) config.headers["X-Brave-Portaria-Id"] = p.id;
      if (p?.nome) config.headers["X-Brave-Portaria-Nome"] = encodeURIComponent(p.nome);
      if (p?.codigo) config.headers["X-Brave-Portaria-Codigo"] = String(p.codigo);
    }
  } catch { /* ignore */ }
  return config;
});

clienteApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      sessionStorage.removeItem("brave_cliente_token");
      sessionStorage.removeItem("brave_cliente_data");
      sessionStorage.removeItem("brave_lgpd_unlock"); // LGPD bloqueado novamente
      window.location.href = "/cliente";
    }
    return Promise.reject(error);
  }
);

// ── Coalescing de GET (dedup de chamadas idênticas em janela curta) ──────────
// Evita que o MESMO GET dispare 2x quando o Layout e a página pedem o mesmo
// dado quase ao mesmo tempo no carregamento (ex.: agendas/count-hoje). Duas
// defesas: (1) se uma requisição idêntica já está em andamento, as demais
// compartilham a MESMA promise; (2) a resposta fica cacheada por uma janela
// curta (COALESCE_TTL_MS), então um 2º pedido logo em seguida a reaproveita.
// Afeta SOMENTE GET simples — mutações (POST/PUT/DELETE), downloads (blob) e
// requisições canceláveis passam direto. Não altera os timers de polling.
const COALESCE_TTL_MS = 2500;
const _inflight = new Map(); // key -> Promise em andamento
const _recent = new Map();   // key -> { res, ts } resposta recente

function _coalesceKey(url, config) {
  let portaria = "";
  try {
    const raw = sessionStorage.getItem("brave_cliente_portaria");
    if (raw) portaria = JSON.parse(raw)?.id || "";
  } catch { /* ignore */ }
  const params = config && config.params ? JSON.stringify(config.params) : "";
  return `${portaria}|${url}|${params}`;
}

function _coalescable(config) {
  if (!config) return true;
  if (config.responseType && config.responseType !== "json") return false; // blob/stream
  if (config.signal || config.cancelToken) return false; // cancelável
  if (config.__noCoalesce) return false; // escape hatch explícito
  return true;
}

const _originalGet = clienteApi.get.bind(clienteApi);
clienteApi.get = function (url, config) {
  if (!_coalescable(config)) return _originalGet(url, config);
  const key = _coalesceKey(url, config);

  // (1) resposta recente dentro da janela → reaproveita sem bater na API
  const hit = _recent.get(key);
  if (hit && Date.now() - hit.ts < COALESCE_TTL_MS) {
    return Promise.resolve(hit.res);
  }
  // (2) requisição idêntica em andamento → compartilha a mesma promise
  const pending = _inflight.get(key);
  if (pending) return pending;

  // (3) dispara de fato (passa pelos interceptors normalmente)
  const p = _originalGet(url, config)
    .then((res) => {
      _recent.set(key, { res, ts: Date.now() });
      setTimeout(() => _recent.delete(key), COALESCE_TTL_MS);
      return res;
    })
    .finally(() => {
      _inflight.delete(key); // erros NÃO são cacheados (só sai de _inflight)
    });
  _inflight.set(key, p);
  return p;
};

export default clienteApi;
